import postRobot from 'post-robot';
import { error, ValidationError } from 'common/errors';
import { getState, saveMeta } from 'communicator/store';
import {
  getAccessToken,
  getApiKey,
  getDpaLocale,
  getIsFeatureToggleEnabled,
  getMerchantCountryCode,
  getMeta,
  getSelectCardPutIntentPayload,
  getUnBindAppInstance,
  getVIntentId,
  isTransactionFidoEligible
} from 'communicator/store/selectors';
import { fetchPutIntent, FetchPutIntentOptions } from 'communicator/utils/api/intent';
import { markEnd, markStart } from 'communicator/utils/instrumentation';
import { AuthenticationDecision, WindowType } from 'types/data';
import { AuthenticationMethod } from 'types/emvco';
import {
  CheckoutCommunicatorPayload,
  CheckoutData,
  CheckoutRequest,
  CheckoutRequestMetadata
} from 'types/visa-sdk/checkout';
import completeCheckoutLegacy from './legacy/complete-checkout-legacy';

// Select Card Flow - Returning or recognized user
export default async function checkoutIntegratedSelectCard(
  checkoutData: CheckoutData,
  launchSRCBankApp: (authenticationMethod: AuthenticationMethod) => void,
  launchSRCIssuerOtp: (authenticationMethod: AuthenticationMethod) => void,
  launchCvv: () => void,
  launchDcf: () => void,
  launchFIDO: (fidoAppScreen: 'auth' | 'register') => void,
  launchThreeDs: (idvUrl: string, windowType?: WindowType) => void,
  launchThreeDsWithOnLoad: (idvUrl: string) => void,
  launchVpp: (
    requestPayload: string,
    checkoutUrl: string,
    authDecision: AuthenticationDecision
  ) => void,
  request: CheckoutRequest,
  meta?: CheckoutRequestMetadata
): Promise<CheckoutCommunicatorPayload | ValidationError> {
  try {
    //START GENAI@CHATGPT4
    let vIntentId = getVIntentId();
    const isSplitSessionEnabled = getIsFeatureToggleEnabled('sdkSplitSession');
    const isSplit = getState().meta.split;

    /* eslint-disable */
    if (isSplitSessionEnabled && isSplit) {
      vIntentId = getVIntentId() || checkoutData?.srcCorrelationId;
    }
    /* eslint-enable */

    if (!vIntentId || !checkoutData.srcDigitalCardId) {
      markEnd('CHECKOUT_INTEGRATED', {
        doFlush: true,
        failure: true,
        reason: 'Unable to connect to DCF'
      });

      return error('Unable to connect to DCF');
    }

    const cardIdMapping = getMeta().cardIdMapping;
    const selectedCardId =
      cardIdMapping[checkoutData.srcDigitalCardId] ?? checkoutData.srcDigitalCardId;

    saveMeta({ selectedCardId });

    const accessToken = getAccessToken()?.token as string;

    markStart('checkout_integrated_put_intent');

    if (meta) {
      saveMeta({ checkoutRequestMetadata: meta });

      if (vIntentId) {
        saveMeta({ vIntentId });
      }

      //END GENAI@CHATGPT4
    }

    const putIntentRequestPayload: FetchPutIntentOptions = {
      accessToken,
      apikey: getApiKey(),
      requestPayload: await getSelectCardPutIntentPayload(selectedCardId, meta),
      vIntentId,
      vsbFlow: checkoutData.vsbFlow || false
    };

    const executeNoLoaderFlows = () => {
      request.launchHandlers.launchStepupLoaderOnWindowRef({
        dpaLocale: getDpaLocale(),
        isCtpDomain: getIsFeatureToggleEnabled('stepupAppDomain'),
        merchantCountryCode: getMerchantCountryCode(),
        windowRef: checkoutData.windowRef
      });

      const controller = new AbortController();
      const { signal } = controller;

      try {
        const listener = postRobot.on('visa-sdk:terminateCvvValidation', () => {
          controller.abort();
          listener.cancel();
          return {
            payload: {
              dcfActionCode: 'CHANGE_CARD',
              unbindAppInstance: getUnBindAppInstance()
            }
          };
        });
      } catch (_) {
        // eslint-disable-next-line no-console
        console.info('Call aborted');
      }

      putIntentRequestPayload.signal = signal;
    };

    /* This logic is to abort the API intent call if the user clicks Cancel on the loader screen */
    // START GENAI@CHATGPT4

    if (getIsFeatureToggleEnabled('enableLoaderRemoval')) {
      if (
        getIsFeatureToggleEnabled('enableNonDcfCvv') &&
        !isTransactionFidoEligible() &&
        !checkoutData.windowGrant
      ) {
        executeNoLoaderFlows();
      }
    } else if (getIsFeatureToggleEnabled('enableNonDcfCvv') && !isTransactionFidoEligible()) {
      executeNoLoaderFlows();
    }

    // END GENAI@CHATGPT4

    const putIntentResponse = await fetchPutIntent(putIntentRequestPayload);
    const checkoutResponse = await completeCheckoutLegacy(
      { checkoutData, putIntentResponse },
      launchSRCBankApp,
      launchSRCIssuerOtp,
      launchCvv,
      launchDcf,
      launchFIDO,
      launchThreeDs,
      launchThreeDsWithOnLoad,
      launchVpp
    );

    if (checkoutResponse.error) {
      markEnd('CHECKOUT_INTEGRATED', {
        doFlush: true,
        failure: true,
        reason: 'CHECKOUT_FAILURE'
      });
    } else {
      markEnd('CHECKOUT_INTEGRATED', { doFlush: true });
    }

    return checkoutResponse;
  } catch (err: unknown) {
    // START GENAI@GHCOPILOT
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const sErr = JSON.stringify(err) !== undefined ? JSON.stringify(err) : 'Unknown error';
    markEnd('CHECKOUT_INTEGRATED', { doFlush: true, failure: true, reason: sErr });
    return error('CHECKOUT_FAILURE', sErr);
    // END GENAI@GHCOPILOT
  }
}
